import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/DashboardLayout";
import { useAuth } from "@/lib/AuthContext";
import { useCountry } from "@/lib/CountryContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import AddressInput from "@/components/AddressInput";
import { toast } from "sonner";
import {
    User,
    Users,
    Building2,
    CreditCard,
    ExternalLink,
} from "lucide-react";

const REGIONS = [
    { value: "HK", label: "Hong Kong SAR" },
    { value: "MO", label: "Macao SAR" },
    { value: "SG", label: "Singapore" },
    { value: "TW", label: "Taiwan" },
    { value: "CN", label: "Mainland China" },
    { value: "OTHER", label: "Other" },
];

const GENDERS = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "nonbinary", label: "Non-binary" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const MEMBERSHIP_LABEL = {
    individual: { icon: User, label: "Individual" },
    family: { icon: Users, label: "Family" },
    business: { icon: Building2, label: "Business" },
};

// Build the form state from the user object,
// filling in defaults for any missing fields.
function buildForm(user) {
    const p = user?.profile || {};
    return {
        name: user?.name || "",
        email: user?.email || "",
        company_name: user?.company_name || "",
        country: user?.country || "HK",
        gender: p.gender || "",
        phone: p.phone || "",
        region: p.region || "HK",
        date_of_birth: p.date_of_birth || "",
        addr: {
            address_line: p.address_line || "",
            address_place_id: p.address_place_id,
            address_lat: p.address_lat,
            address_lng: p.address_lng,
            address_components: p.address_components,
        },
    };
}

export default function Profile() {
    const { user, refresh } = useAuth();
    // Active-country catalogue so the picker only offers markets we
    // actually support at signup time. Falls back to the user's own
    // country if it happens to have been deactivated after they joined.
    const { countries: activeCountries, allCountries } = useCountry();
    const [saving, setSaving] = useState(false);
    const [portalBusy, setPortalBusy] = useState(false);
    const [form, setForm] = useState(() => buildForm(user));

    // // Route-level nav doesn't remount AuthProvider, so the cached `user`
    // // can go stale (e.g. edited from another tab/session). Pull fresh
    // // data whenever this page is opened.
    // useEffect(() => {
    //     refresh();
    // }, [refresh]);

    // Resyncs the form if `user` changes after mount (e.g. Save below
    // calling refresh()); the initial paint is already correct via the
    // lazy useState initializer above.
    useEffect(() => {
        if (!user) return;
        setForm(buildForm(user));
    }, [user]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                company_name: form.company_name.trim() || null,
                profile: {
                    full_name: form.name.trim(),
                    gender: form.gender || null,
                    phone: form.phone || null,
                    region: form.region,
                    date_of_birth: form.date_of_birth || null,
                    address_line: form.addr.address_line || null,
                    address_place_id: form.addr.address_place_id || null,
                    address_lat: form.addr.address_lat || null,
                    address_lng: form.addr.address_lng || null,
                    address_components: form.addr.address_components || null,
                },
            };
            // Only send `country` when it actually changed — skips a
            // pointless validation round-trip when the member is just
            // updating name/phone/etc and hasn't touched the market.
            if (form.country && form.country !== user?.country) {
                payload.country = form.country;
            }
            await api.patch("/member/profile", payload);
            await refresh();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const openBillingPortal = async () => {
        setPortalBusy(true);
        try {
            const { data } = await api.post("/member/billing-portal", {
                return_url: window.location.href,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(formatApiError(err));
            setPortalBusy(false);
        }
    };

    const mtype = MEMBERSHIP_LABEL[user?.membership_type || "individual"];
    const MTypeIcon = mtype.icon;

    return (
        <>
            <PageHeader
                eyebrow="Account"
                title="Your Perx profile"
                description="Update your contact details, address and payment method."
                testid="profile-header"
            />

            <div className="px-6 lg:px-12 py-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
                {/* Left — big form */}
                <form
                    onSubmit={save}
                    data-testid="profile-form"
                    className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-8 space-y-8"
                >
                    <div className="flex items-center gap-4">
                        <div className="h-16 w-16 rounded-full bg-gold-500 text-navy-900 flex items-center justify-center font-display text-2xl">
                            {user?.name?.[0]}
                        </div>
                        <div>
                            <div className="font-display text-xl text-navy-700">
                                {user?.name}
                            </div>
                            <div className="text-sm text-navy-500">{user?.email}</div>
                            <div className="inline-flex items-center gap-1.5 text-xs text-gold-700 bg-gold-50 border border-gold-200 rounded-full px-2.5 py-0.5 mt-1.5">
                                <MTypeIcon size={11} /> {mtype.label} membership
                            </div>
                        </div>
                    </div>

                    <Section eyebrow="01" title="Account details">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Full name">
                                <Input
                                    data-testid="profile-name"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, name: e.target.value }))
                                    }
                                />
                            </Field>
                            <Field label="Email">
                                <Input
                                    type="email"
                                    data-testid="profile-email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, email: e.target.value }))
                                    }
                                />
                            </Field>
                            <BillingCountryField
                                value={form.country}
                                onChange={(v) => setForm((f) => ({ ...f, country: v }))}
                                userCountry={user?.country}
                                activeCountries={activeCountries}
                                allCountries={allCountries}
                            />
                            {user?.membership_type === "business" && (
                                <Field label="Company name" className="sm:col-span-2">
                                    <Input
                                        data-testid="profile-company"
                                        value={form.company_name}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                company_name: e.target.value,
                                            }))
                                        }
                                    />
                                </Field>
                            )}
                        </div>
                    </Section>

                    <Section eyebrow="02" title="Personal details">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Phone">
                                <Input
                                    type="tel"
                                    data-testid="profile-phone"
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, phone: e.target.value }))
                                    }
                                />
                            </Field>
                            <Field label="Gender">
                                <Select
                                    value={form.gender}
                                    onValueChange={(v) =>
                                        setForm((f) => ({ ...f, gender: v }))
                                    }
                                >
                                    <SelectTrigger data-testid="profile-gender" className="h-11">
                                        <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {GENDERS.map((g) => (
                                            <SelectItem key={g.value} value={g.value}>
                                                {g.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Region">
                                <Select
                                    value={form.region}
                                    onValueChange={(v) =>
                                        setForm((f) => ({ ...f, region: v }))
                                    }
                                >
                                    <SelectTrigger data-testid="profile-region" className="h-11">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REGIONS.map((r) => (
                                            <SelectItem key={r.value} value={r.value}>
                                                {r.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Date of birth">
                                <Input
                                    type="date"
                                    data-testid="profile-dob"
                                    value={form.date_of_birth}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            date_of_birth: e.target.value,
                                        }))
                                    }
                                    max={new Date().toISOString().split("T")[0]}
                                />
                            </Field>
                            <Field label="Address" className="sm:col-span-2">
                                <AddressInput
                                    value={form.addr}
                                    onChange={(v) => setForm((f) => ({ ...f, addr: v }))}
                                    testid="profile-address"
                                />
                            </Field>
                        </div>
                    </Section>

                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-navy-50">
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="profile-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>

                {/* Right — payment + secondary members */}
                <div className="space-y-6">
                    <div
                        className="rounded-2xl bg-white border border-gold-200/60 shadow-soft p-6"
                        data-testid="profile-payment-card"
                    >
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-600 mb-2">
                            <CreditCard size={14} /> Payment method
                        </div>
                        <div className="font-display text-xl text-navy-700 leading-tight">
                            Manage your card & invoices
                        </div>
                        <p className="text-navy-500 text-sm mt-2">
                            Update your saved card, download invoices or cancel your
                            subscription on the secure Stripe portal.
                        </p>
                        <Button
                            type="button"
                            onClick={openBillingPortal}
                            disabled={portalBusy}
                            data-testid="profile-manage-payment"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 mt-4 w-full font-semibold"
                        >
                            {portalBusy ? "Opening…" : "Edit payment"}
                            <ExternalLink size={14} className="ml-2" />
                        </Button>
                    </div>

                    <div className="rounded-2xl bg-navy-700 text-white p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-2">
                            Family
                        </div>
                        <h3 className="font-display text-2xl leading-tight">
                            Secondary members
                        </h3>
                        <p className="text-navy-50/70 text-sm mt-2">
                            Add your partner, parents or dependants. Send a QR-code
                            payment link so they can pay for their own perks.
                        </p>
                        <Link to="/member/family">
                            <Button
                                data-testid="profile-manage-family"
                                className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold mt-4 w-full"
                            >
                                Manage secondary members
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
}

function Section({ eyebrow, title, children }) {
    return (
        <section className="space-y-3">
            <div className="flex items-baseline gap-3">
                <span className="text-xs font-mono text-gold-600 tracking-widest">
                    {eyebrow}
                </span>
                <h2 className="font-display text-xl text-navy-700 leading-none">
                    {title}
                </h2>
            </div>
            {children}
        </section>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={className}>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
            </Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}


/**
 * Billing country picker + relocation warning.
 *
 * The user's persisted `country` field drives which Stripe Price (and
 * currency) their membership + deal checkouts resolve to. When a member
 * relocates from HK → SG we want to let them switch here rather than
 * open a support ticket. Two guardrails:
 *   1. Only ACTIVE markets are offered (from CountryContext) so members
 *      can't pick a country we don't yet have a Stripe catalogue in.
 *      If their current country was deactivated after they joined we
 *      still show it as read-only so they see what they're on.
 *   2. Amber warning appears when the pending value differs from the
 *      persisted user.country — reminds them that existing subscription
 *      renewals stay on the old Price until Stripe re-bills.
 */
function BillingCountryField({ value, onChange, userCountry, activeCountries, allCountries }) {
    const options = (activeCountries || []).slice();
    // Include the user's current country in the dropdown even if it's
    // been deactivated — otherwise the Select would show a blank value.
    if (userCountry && !options.find((c) => c.code === userCountry)) {
        const fallback = (allCountries || []).find((c) => c.code === userCountry);
        if (fallback) options.push({ ...fallback, _deactivated: true });
    }
    const changed = value && userCountry && value !== userCountry;
    const currentMeta = options.find((c) => c.code === value);

    return (
        <Field label="Billing country" className="sm:col-span-2">
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid="profile-country" className="h-11">
                    <SelectValue>
                        {currentMeta ? (
                            <span className="flex items-center gap-2">
                                <span>{currentMeta.flag}</span>
                                <span>{currentMeta.name}</span>
                                <span className="text-navy-400 text-xs">
                                    · {currentMeta.currency}
                                </span>
                                {currentMeta._deactivated && (
                                    <span className="text-[10px] text-amber-600 uppercase tracking-widest">
                                        (deactivated)
                                    </span>
                                )}
                            </span>
                        ) : (
                            value
                        )}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {options.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.name} · {c.currency}
                            {c._deactivated ? " (deactivated)" : ""}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <p className="text-[11px] text-navy-500 mt-2 leading-relaxed">
                This is the market your future membership renewals and new deal
                purchases are billed against. Any active subscription stays on the
                currency it started in until it next renews.
            </p>
            {changed && (
                <div
                    data-testid="profile-country-warning"
                    className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 leading-relaxed"
                >
                    <strong>Heads up:</strong> switching {userCountry} → {value}{" "}
                    changes your billing currency on next renewal. If you have an
                    active subscription, cancel-and-rejoin from the payment portal to
                    switch immediately.
                </div>
            )}
        </Field>
    );
}
